import {yupResolver} from "@hookform/resolvers/yup"
import {Box, Button, Grid, TextField, Typography} from "@mui/material"
import React, {useState} from "react"
import {Controller, useForm} from "react-hook-form"
import * as yup from "yup"
import {AppButton, AppSecondaryButton} from "@app-core/AppButton"
import {LOGO_BLUE} from "theme"

export interface SoftExitContact {
  navigatorReviewRequested: boolean
  name: string
  email: string
  phone: string
}

const schema = yup.object().shape({
  wantsContact: yup.boolean().required(),
  email: yup
    .string()
    .email("You must enter a valid email")
    .required("You must enter an email"),
  name: yup.string().when("wantsContact", {
    is: true,
    then: yup.string().required("You must enter your name"),
    otherwise: yup.string()
  }),
  phone: yup.string().when("wantsContact", {
    is: true,
    then: yup.string().required("You must enter a phone number"),
    otherwise: yup.string()
  })
})

interface EligibilitySoftExitProps {
  onSubmit: (contact: SoftExitContact) => void
  onBack: () => void
  isSubmitting?: boolean
}

export const EligibilitySoftExit: React.FC<EligibilitySoftExitProps> = (
  props: EligibilitySoftExitProps
) => {
  const {onSubmit, onBack, isSubmitting = false} = props
  const [wantsContact, setWantsContact] = useState<boolean | null>(null)

  const {control, handleSubmit, setValue, formState} = useForm({
    mode: "onChange",
    defaultValues: {wantsContact: false, name: "", email: "", phone: ""},
    resolver: yupResolver(schema)
  })
  const {errors, isValid} = formState

  const onSelectContact = (value: boolean) => {
    setWantsContact(value)
    setValue("wantsContact", value, {shouldValidate: true})
  }

  const submit = handleSubmit((data) => {
    onSubmit({
      navigatorReviewRequested: data.wantsContact,
      name: data.wantsContact ? data.name : "",
      email: data.email,
      phone: data.wantsContact ? data.phone : ""
    })
  })

  const choiceButtonSx = (selected: boolean) => ({
    borderRadius: "17px",
    minHeight: "56px",
    fontSize: "20px",
    color: "#707070",
    border: selected ? `4px solid ${LOGO_BLUE}` : "1px solid #707070",
    ":hover": {border: `1px solid ${LOGO_BLUE}`}
  })

  return (
    <Box padding={4}>
      <Box paddingX={{xs: 0, sm: 6}}>
        <Typography
          fontWeight="bold"
          variant="h4"
          gutterBottom
          component="div"
          fontSize="24px"
        >
          Based on your answers, we&apos;d like to learn more about your
          situation before determining your eligibility.
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          component="div"
          fontSize="18px"
          marginTop={2}
        >
          Many people qualify for the Disability Tax Credit but don&apos;t
          realize it from a short check alone. A Benefits2 navigator can review
          your situation and help you understand your options.
        </Typography>
      </Box>

      <Box paddingX={{xs: 0, sm: 6}} marginTop={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              sx={choiceButtonSx(wantsContact === true)}
              onClick={() => onSelectContact(true)}
            >
              Yes, Connect Me with a Navigator
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              sx={choiceButtonSx(wantsContact === false)}
              onClick={() => onSelectContact(false)}
            >
              No, Thank You
            </Button>
          </Grid>
        </Grid>
      </Box>

      {wantsContact !== null && (
        <Box paddingX={{xs: 0, sm: 6}} marginTop={3}>
          <Grid container spacing={2}>
            {wantsContact && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="name"
                  render={({field}) => (
                    <TextField
                      {...field}
                      label="Name"
                      placeholder="Name"
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors?.name?.message}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    label="Email"
                    placeholder="Email"
                    variant="outlined"
                    fullWidth
                    required
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                  />
                )}
              />
            </Grid>
            {wantsContact && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="phone"
                  render={({field}) => (
                    <TextField
                      {...field}
                      label="Phone"
                      placeholder="Phone"
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.phone}
                      helperText={errors?.phone?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Box
        display={"flex"}
        alignItems="center"
        justifyContent="flex-end"
        gap={2}
        marginTop={4}
        paddingX={{xs: 0, sm: 6}}
      >
        <AppSecondaryButton onClick={onBack} sx={{minWidth: "120px"}}>
          Back
        </AppSecondaryButton>
        <AppButton
          onClick={submit}
          disabled={wantsContact === null || !isValid || isSubmitting}
          sx={{minWidth: "150px"}}
        >
          Submit
        </AppButton>
      </Box>
    </Box>
  )
}

export default EligibilitySoftExit
